const PARTICIPANT_PROFILE_STORAGE_KEY = "band-sync-lab:participant-profile:v1";
const TAB_PARTICIPANT_ID_STORAGE_KEY = "band-sync-lab:tab-participant-id:v1";

export interface ParticipantProfile {
  readonly participantId: string;
  readonly nickname: string;
}

function generateParticipantId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `participant-${crypto.randomUUID()}`;
  }
  const fallback = Math.random().toString(36).slice(2, 10);
  return `participant-${fallback}`;
}

function randomSuffix(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

function normalizeNickname(raw: string | undefined): string {
  const trimmed = raw?.trim() ?? "";
  if (!trimmed) {
    return "Guest";
  }
  return trimmed.slice(0, 40);
}

export function loadParticipantProfile(): ParticipantProfile {
  if (typeof window === "undefined") {
    return {
      participantId: generateParticipantId(),
      nickname: "Guest",
    };
  }
  const raw = window.localStorage.getItem(PARTICIPANT_PROFILE_STORAGE_KEY);
  if (!raw) {
    const initial = {
      participantId: generateParticipantId(),
      nickname: "Guest",
    } satisfies ParticipantProfile;
    saveParticipantProfile(initial);
    return initial;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<ParticipantProfile>;
    const participantId =
      typeof parsed.participantId === "string" && parsed.participantId.trim().length > 0
        ? parsed.participantId.trim()
        : generateParticipantId();
    const nickname = normalizeNickname(parsed.nickname);
    const nextProfile = { participantId, nickname } satisfies ParticipantProfile;
    saveParticipantProfile(nextProfile);
    return nextProfile;
  } catch {
    const repaired = {
      participantId: generateParticipantId(),
      nickname: "Guest",
    } satisfies ParticipantProfile;
    saveParticipantProfile(repaired);
    return repaired;
  }
}

export function saveParticipantProfile(profile: ParticipantProfile): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(PARTICIPANT_PROFILE_STORAGE_KEY, JSON.stringify(profile));
}

export function updateParticipantNickname(
  profile: ParticipantProfile,
  nickname: string,
): ParticipantProfile {
  const next = {
    participantId: profile.participantId,
    nickname: normalizeNickname(nickname),
  } satisfies ParticipantProfile;
  saveParticipantProfile(next);
  return next;
}

export function loadOrCreateTabParticipantId(baseParticipantId: string): string {
  if (typeof window === "undefined") {
    return `${baseParticipantId}-tab-${randomSuffix()}`;
  }
  const existing = window.sessionStorage.getItem(TAB_PARTICIPANT_ID_STORAGE_KEY)?.trim();
  if (existing) {
    return existing;
  }
  const created = `${baseParticipantId}-tab-${randomSuffix()}`;
  window.sessionStorage.setItem(TAB_PARTICIPANT_ID_STORAGE_KEY, created);
  return created;
}

export function saveTabParticipantId(participantId: string): string {
  const normalized = participantId.trim();
  if (!normalized) {
    return normalized;
  }
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(TAB_PARTICIPANT_ID_STORAGE_KEY, normalized);
  }
  return normalized;
}

export function regenerateTabParticipantId(baseParticipantId: string): string {
  const regenerated = `${baseParticipantId}-tab-${randomSuffix()}`;
  if (typeof window !== "undefined") {
    window.sessionStorage.setItem(TAB_PARTICIPANT_ID_STORAGE_KEY, regenerated);
  }
  return regenerated;
}
